import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CardImage } from "@/components/CardImage";
import { Star, Lock, PauseCircle, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { DisputeDialog } from "./DisputeDialog";

const fmt = (v) => (v ? new Date(v).toLocaleDateString("it-IT") : "—");
const OUTCOME = { riprendi: "Lo scambio è ripreso", annulla: "Scambio annullato dalla moderazione", chiusa: "Contestazione chiusa" };

function FeedbackDialog({ pid, otherUsername, open, onOpenChange, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/feedback", { proposal_id: pid, rating, comment }); toast.success("Feedback registrato. Sarà pubblicato secondo le regole della community."); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]" data-testid="feedback-dialog">
        <DialogHeader><DialogTitle className="font-display">Valuta @{otherUsername}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Il voto non è modificabile e resta nascosto finché anche l'altra parte non ha votato, oppure per 14 giorni dal completamento.</DialogDescription></DialogHeader>
        <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rating-${n}`} aria-label={`${n} stelle`}><Star className={`h-8 w-8 transition-transform hover:scale-110 ${n <= rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#475569]"}`} /></button>)}</div>
        <Textarea data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} placeholder="Commento facoltativo: com'è andato lo scambio?" className="bg-[#0B1020] border-[#2A3655]" />
        <DialogFooter><Button onClick={submit} disabled={busy || !rating} data-testid="submit-feedback" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Invia valutazione</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisputeItem({ d }) {
  return (
    <div className="rounded-lg border border-[#2A3655] bg-[#0B1020] p-3" data-testid="dispute-item">
      <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-sm font-semibold text-[#F59E0B]">{d.category_label}</p>
        <span className="text-[11px] text-[#94A3B8]">{fmt(d.created_at)} · {d.status === "aperta" ? "In esame" : "Gestita"}</span></div>
      <p className="mt-1 text-sm text-[#CBD5E1]">{d.description}</p>
      {d.photos?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{d.photos.map((ph) => <div key={ph} className="w-14"><CardImage path={ph} className="w-14" /></div>)}</div>}
      {d.resolution && <p className="mt-2 text-xs text-[#2DD4BF]" data-testid="dispute-resolution">{OUTCOME[d.resolution.outcome]} — {d.resolution.reason}</p>}
    </div>
  );
}

export function ReputationPanel({ pid, status, otherUsername, onChanged }) {
  const [r, setR] = useState(null);
  const [fbOpen, setFbOpen] = useState(false);
  const load = useCallback(() => { api.get(`/reputation/proposal/${pid}`).then((x) => setR(x.data)).catch(() => {}); }, [pid]);
  useEffect(() => { load(); }, [load, status]);
  if (!r) return null;
  const refresh = () => { load(); onChanged?.(); };
  const showFeedback = status === "completata";
  if (!showFeedback && !r.disputes.length && !r.can_dispute) return null;
  return (
    <section className="mt-6 space-y-3 rounded-2xl border border-[#2A3655] bg-[#141B2D] p-4" data-testid="reputation-panel">
      {showFeedback && (
        <div>
          <h3 className="flex items-center gap-2 font-display font-semibold text-[#F8FAFC]"><Star className="h-4 w-4 text-[#F59E0B]" />Valutazione dello scambio</h3>
          {r.mine ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-[#CBD5E1]" data-testid="my-feedback-status">
              {r.mine.published ? <CheckCircle2 className="h-4 w-4 text-[#10B981]" /> : <Lock className="h-4 w-4 text-[#94A3B8]" />}
              Hai dato {r.mine.rating}/5 · {r.mine.published ? "pubblicata" : `in attesa di pubblicazione (entro il ${fmt(r.publish_deadline)} o quando @${otherUsername} avrà votato)`}
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button onClick={() => setFbOpen(true)} data-testid="leave-feedback" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Star className="mr-2 h-4 w-4" />Lascia un feedback</Button>
              <span className="text-xs text-[#94A3B8]">{r.other_left ? `@${otherUsername} ha già votato: il suo voto resta nascosto finché non voti anche tu.` : "Puoi votare fino alla pubblicazione automatica."}</span>
            </div>
          )}
          {r.suspended && <p className="mt-2 flex items-center gap-2 text-xs text-[#F59E0B]" data-testid="feedback-suspended"><PauseCircle className="h-4 w-4" />Pubblicazione dei feedback sospesa: c'è una contestazione in esame.</p>}
        </div>
      )}
      {(r.disputes.length > 0 || r.can_dispute) && (
        <div>
          <h3 className="flex items-center gap-2 font-display font-semibold text-[#F8FAFC]"><ShieldAlert className="h-4 w-4 text-[#F59E0B]" />Contestazioni</h3>
          <div className="mt-2 space-y-2">{r.disputes.map((d) => <DisputeItem key={d.id} d={d} />)}</div>
          {r.can_dispute && <div className="mt-2"><DisputeDialog pid={pid} categories={r.categories} onDone={refresh} /></div>}
        </div>
      )}
      <FeedbackDialog pid={pid} otherUsername={otherUsername} open={fbOpen} onOpenChange={setFbOpen} onDone={refresh} />
    </section>
  );
}
